"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PerformanceTableStyle = void 0;
/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importDefault(require("react"));
const react_virtuoso_1 = require("react-virtuoso");
const react_i18next_1 = require("react-i18next");
const styled_components_1 = require("../styled-components");
const EmptyState_1 = require("../EmptyState");
const Icons_1 = require("../Icons");
/** Spacing between rows of first-level table */
const FIXED_SPACE = 5;
/** Maximum height for inner table */
const SUB_TABLE_HEIGHT = 300;
/** Height of second level header */
const SUB_TABLE_HEADER_HEIGHT = 32;
/** Height of third level table rows and headers */
const SUB_SUB_TABLE_ROW_HEIGHT = 28;
/** Spacing between rows of sub-tables */
const SUB_TABLE_PADDING = 2;
/** Height of first and second level table rows */
const ROW_HEIGHT = 40;
/** Similar to the generic Flex component with noWrap and noOverflow, but has fewer options. The lighten and darken colours are less opaque. */
const Flex = styled_components_1.styled.div `
  display: flex;
  background: ${(props) => props.darken ? "#0000000a" : props.lighten ? "#ffffff0d" : null};
  flex-direction: ${(props) => (props.col ? "column" : "row")};
  align-items: ${(props) => props.align || "stretch"};
  justify-content: ${(props) => props.justify || "stretch"};
  flex-wrap: nowrap;
  flex: 1 1 auto;
  overflow: hidden;
  border-radius: ${(props) => (props.rounded ? props.theme.radius : 0)};
`;
/** Grid for a table row – just add grid-template-columns */
const RowGrid = styled_components_1.styled.div `
  display: grid;
  align-self: stretch;
  align-items: stretch;
  flex-wrap: nowrap;
  width: 100%;
  overflow: hidden;
`;
const BaseCell = styled_components_1.styled.div `
  display: flex;
  align-self: stretch;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;
/** Generic table cell styled div, with left and right borders */
const Cell = (0, styled_components_1.styled)(BaseCell) `
  padding: ${FIXED_SPACE}px ${({ theme }) => theme.gutter};
  border-right: 1px solid #0004;
  border-left: 1px solid #fff3;
  ${({ firstColumn, theme }) => firstColumn &&
    `border-left: none;
    border-bottom-left-radius: ${theme.radius};
    border-top-left-radius: ${theme.radius};`}
  ${({ lastColumn, theme }) => lastColumn &&
    `border-right: none;
    border-bottom-right-radius: ${theme.radius};
    border-top-right-radius: ${theme.radius};`}
  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    padding: ${FIXED_SPACE}px;
  }
`;
/** Cell with no padding; default height is ROW_HEIGHT */
const InnerTableCell = (0, styled_components_1.styled)(Cell) `
  height: ${({ height }) => height !== null && height !== void 0 ? height : ROW_HEIGHT}px;
  background: ${({ selected }) => (selected ? "#ffffff26" : "#ffffff0d")};
  flex: 1 1 100%;
  justify-content: center;
  white-space: nowrap;
  margin-top: ${SUB_TABLE_PADDING}px;
  padding: 0;
`;
/** Cell with padding, left-aligned */
const InnerTableTextCell = (0, styled_components_1.styled)(InnerTableCell) `
  justify-content: start;
  padding: ${SUB_TABLE_PADDING}px ${({ theme }) => theme.gutter};
`;
/** Cell with padding, right-aligned */
const InnerTableTotalCell = (0, styled_components_1.styled)(InnerTableTextCell) `
  justify-content: flex-end;
  text-align: right;
  flex: 1 0 auto;
`;
/** Header cell with no padding */
const HeaderCell = (0, styled_components_1.styled)(BaseCell) `
  white-space: nowrap;
`;
/** Right-aligned cell used for numeric values */
const TotalCell = (0, styled_components_1.styled)(BaseCell) `
  justify-content: flex-end;
  flex: 1 0 auto;
`;
/** Numeric value shown in a TotalCell in a table */
const Total = styled_components_1.styled.output `
  color: ${({ colour }) => (colour ? colour : "inherit")};
  vertical-align: middle;
  text-overflow: ellipsis;
  display: flex;
  justify-content: flex-end;
`;
/** Fixed height table row container */
const FixedHeight = (0, styled_components_1.styled)(Flex) `
  height: ${ROW_HEIGHT}px;
  width: 100%;
  flex: 0 0 ${ROW_HEIGHT}px;
  align-items: center;
`;
/** Small spacer div */
const FixedSpace = styled_components_1.styled.div `
  flex: 0 0 ${FIXED_SPACE}px;
  height: ${FIXED_SPACE}px;
  width: ${FIXED_SPACE}px;
  display: block;
`;
/** Very small spacer div */
const InnerTableSpace = styled_components_1.styled.div `
  flex: 0 0 ${SUB_TABLE_PADDING}px;
  height: ${SUB_TABLE_PADDING}px;
  width: ${SUB_TABLE_PADDING}px;
  display: block;
`;
/** Top level table header */
const TableHeader = (0, styled_components_1.styled)(Flex) `
  flex-direction: column;
  flex-wrap: nowrap;
  overflow: hidden;
  background: ${({ theme }) => theme.panel};
  border-radius: 0;
  /* Shadow is grey, not black, so that it doesn't show up on the background */
  box-shadow: 0 2px 3px #282828aa;
  z-index: 1;
  height: ${ROW_HEIGHT}px;
  margin-top: ${({ theme }) => theme.gutter};
`;
const EmptyStateFloater = styled_components_1.styled.div `
  position: absolute;
  height: ${SUB_TABLE_HEIGHT}px;
  width: 100%;
  pointer-events: none;
`;
const FixedHeader = styled_components_1.styled.div `
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  margin: 0 ${({ theme }) => theme.gutter} 0;
  overflow: visible;
`;
const PerformanceTableWrapper = styled_components_1.styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ theme }) => theme.screen.small}) {
    & > div > div > div {
      border-radius: 0;
    }
  }
  overflow: visible;
`;
/** Virtual list using Virtuoso */
const StyledList = (0, styled_components_1.styled)(react_virtuoso_1.Virtuoso) `
  flex: 1 1 auto;
  min-height: 0;
  /* Ensure the scroller is contained within the component bounds */
  contain: size layout;
  @media (max-width: ${({ theme }) => theme.screen.small}) {
    & > div > div > div {
      border-radius: 0;
    }
  }
`;
/** Row container for a StyledList */
const Item = styled_components_1.styled.div `
  margin: 0 ${({ theme }) => theme.gutter};
  &:last-child {
    margin-bottom: ${({ theme }) => theme.gutter};
  }
  .no-margin & {
    margin: 0;
  }

  /* Adjust margin when scrollbar is present */
  ${({ hasScrollbar }) => hasScrollbar &&
    `margin-right: 5px;
  `}
`;
/** Open/close chevron marker */
const Expander = ({ open, onClick, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const ref = react_1.default.useRef(null);
    return (react_1.default.createElement(Flex, { ref: ref, justify: "center", tabIndex: 0, onClick: onClick, onKeyDown: (e) => {
            var _a;
            // Allow keyboard activation
            if ([" ", "Enter"].includes(e.key)) {
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.click();
                e.preventDefault();
                e.stopPropagation();
            }
        } }, open ? (react_1.default.createElement(Icons_1.ChevronUp, { title: t("button-Collapse") })) : (react_1.default.createElement(Icons_1.ChevronDown, { title: t("button-Expand") }))));
};
/** Virtual table style used on JobPerformance and MachinePerformance pages */
const PerformanceTable = ({ listHeight, itemCount, renderHeader, renderItem, itemStyle, headerStyle, className, }) => {
    const virtuoso = react_1.default.useRef(null);
    const scrollerRef = react_1.default.useRef(null);
    const [hasScrollbar, setHasScrollbar] = react_1.default.useState(false);
    // Virtuoso workaround, see https://github.com/petyosi/react-virtuoso/issues/274
    const setScrollerRef = react_1.default.useCallback((ref) => {
        scrollerRef.current = ref;
        // Check for scrollbar presence
        if (ref) {
            const checkScrollbar = () => {
                const hasVerticalScrollbar = ref.scrollHeight > ref.clientHeight;
                setHasScrollbar(hasVerticalScrollbar);
            };
            // Initial check
            checkScrollbar();
            // Check on resize
            const resizeObserver = new ResizeObserver(checkScrollbar);
            resizeObserver.observe(ref);
            // Cleanup
            return () => resizeObserver.disconnect();
        }
    }, []);
    const ItemWrapper = react_1.default.useCallback((props) => react_1.default.createElement(Item, { ...props, hasScrollbar: hasScrollbar }), [hasScrollbar]);
    if (itemCount <= 0) {
        return react_1.default.createElement(EmptyState_1.EmptyState, null);
    }
    const headerHeight = ROW_HEIGHT + FIXED_SPACE;
    const adjustedListHeight = listHeight ? listHeight - headerHeight : undefined;
    return (react_1.default.createElement(PerformanceTableWrapper, { className: className },
        react_1.default.createElement(FixedHeader, { style: headerStyle }, renderHeader(0)),
        react_1.default.createElement(StyledList, { ref: virtuoso, scrollerRef: setScrollerRef, defaultItemHeight: ROW_HEIGHT + FIXED_SPACE, height: adjustedListHeight, totalCount: itemCount - 1, components: { Item: ItemWrapper }, itemContent: (index) => renderItem(index + 1, itemStyle), style: {
                minHeight: listHeight,
                maxHeight: adjustedListHeight || "100%",
                overflowX: "hidden",
                overflowY: "auto",
            } })));
};
exports.PerformanceTableStyle = {
    FIXED_SPACE,
    SUB_TABLE_HEIGHT,
    SUB_TABLE_HEADER_HEIGHT,
    SUB_SUB_TABLE_ROW_HEIGHT,
    SUB_TABLE_PADDING,
    ROW_HEIGHT,
    Cell,
    EmptyStateFloater,
    Expander,
    FixedHeight,
    FixedSpace,
    HeaderCell,
    InnerTableCell,
    InnerTableSpace,
    InnerTableTextCell,
    InnerTableTotalCell,
    PerformanceTable,
    PerformanceTableFlex: Flex,
    PerformanceTableWrapper,
    RowGrid,
    TableHeader,
    Total,
    TotalCell,
};
//# sourceMappingURL=index.js.map